import {
	Card,
	FormGroup,
	ListGroup,
	Button,
	FloatingLabel,
	ButtonGroup,
} from "react-bootstrap";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import "./MainView.css";
import { useState, useEffect } from "react";
import generateSequences from "./scripts/GenerateSequences";
import config from "./assets/config.json"//"./assets/config_local.json";
import { IndexedFasta } from '@gmod/indexedfasta'
import { openLocation } from '@jbrowse/core/util/io'

// TODO: This default form should use values from config
let genome = Object.keys(config)[0]
const form = {
	referenceGenome: {
		name: genome,
		version: "Version 1",
	},
	region: {
		chr: "Chr03",
		start: "5792722",
		end: "5792922"
	},
	individuals: [],
	regionViewLimit: 100000,
}

function MainView({ onFastaGenerated, onFormUpdated, onAnnotationsLoaded }) {

	// State variable

	// Stores the selected reference genome
	const [genome, setGenome] = useState(Object.keys(config)[0]);
	const [customFASTA, setCustomFASTA] = useState(null);
	const [customFAI, setCustomFAI] = useState(null);
	const [validCustom, setValidCustom] = useState(false);

	// Stores the available chromosomes for the selected reference genome
	const [chromosomes, setChromosomes] = useState([]);
	// Stores the length of each chromosome
	const [chrLengths, setChrLengths] = useState({});
	// Stores the selected analysis version
	const [version, setVersion] = useState("");
	// Stores the available samples for the selected analysis version
	const [samples, setSamples] = useState([]);
	// Stores the available species for the selected analysis version
	const [species, setSpecies] = useState([]);

	// Stores if the selected region is valid
	const [startValidated, setStartValidated] = useState(true);
	const [endValidated, setEndValidated] = useState(true);

	// Stores the selected individuals
	const [checkedAll, setCheckedAll] = useState(false);
	const [checked, setChecked] = useState({
		...Array(samples.length).fill(false),
	});
	const [selected, setSelected] = useState(species[0]);

	// Stores the progress of the data retrieval
	const [progress, setProgress] = useState(0);

	// Stores the selected region
	const [chromosome, setChromosome] = useState(form.region.chr);
	const [start, setStart] = useState(form.region.start);
	const [end, setEnd] = useState(form.region.end);

	// Stores the custom VCF and TBI files
	const [customVCF, setCustomVCF] = useState(null);
	const [customTBI, setCustomTBI] = useState(null);

	// Advanced settings
	const [minDepth, setMinDepth] = useState(15);
	const [useSNPs, setUseSNPs] = useState(true);
	const [useIndels, setUseIndels] = useState(true);

	const toggleCheck = (inputName) => {
		setChecked((prevState) => {
			const newState = { ...prevState };
			newState[inputName] = !prevState[inputName];
			return newState;
		});
	};

	// For selecting all visible samples
	const selectAll = (value) => {
		setCheckedAll(value);
		setChecked((prevState) => {
			const newState = { ...prevState };
			Object.keys(prevState).forEach((key) => {
				if (
					selected === "All species" ||
					samples[key].species === selected
				)
					newState[key] = value;
			});
			return newState;
		});
	};
	const updateCheckedAll = (value) => {
		let allChecked = true;
		for (const inputName in checked) {
			if (
				value === "All species" ||
				samples[inputName].species === value
			)
				if (checked[inputName] === false) {
					allChecked = false;
					break;
				}
		}
		if (allChecked) {
			setCheckedAll(true);
		} else {
			setCheckedAll(false);
		}
	};
	useEffect(() => {
		if (config[genome]["versions"]) {
			const versions = Object.keys(config[genome]["versions"]);
			setVersion(versions[0]);
		}
		// Load FASTA file to detect available chromosomes
		const t = new IndexedFasta({
			fasta: openLocation({
				uri: config[genome].files['fastaFile'],
				locationType: 'UriLocation'
			}),
			fai: openLocation({
				uri: config[genome].files['faiFile'],
				locationType: 'UriLocation'
			}),
			chunkSizeLimit: Number.MAX_VALUE,
		})
		t.getSequenceNames().then((seqs) => {
			setChromosomes(seqs);
			setChromosome(seqs[0]);
		});
		t.getSequenceSizes().then((sizes) => {
			setChrLengths(sizes);
		});
	}, [genome]);

	useEffect(() => {
		if (version !== "" && config[genome]["versions"]) {
			setSamples(config[genome]["versions"][version].samples);
			let species = config[genome]["versions"][version].samples
				.map((variant) => variant.species)
				.filter((value, index, self) => self.indexOf(value) === index);
			species.unshift("All species");
			setChecked({
				...Array(config[genome]["versions"][version].samples.length).fill(false),
			});
			setSpecies(species);
			setSelected(species[0]);
		}
		else {
			setSamples([]);
			setSpecies([]);
		}
	}, [version, genome]);

	useEffect(() => {
		updateCheckedAll(selected);
	}, [checked, selected]);

	useEffect(() => {
		setEndValidated(end > start && end <= chrLengths[chromosome]);
		setStartValidated(start >= 1 && start < end);

	}, [start, end, chromosome, chrLengths, chromosomes]);

	useEffect(() => {
		const valid = customFASTA && customFAI && customFASTA!="" && customFAI!="";
		setValidCustom(valid);

	}, [customFASTA, customFAI]);
	// Submit form and generate FASTA files
	const submit = () => {

		onFastaGenerated({}); // Clear previous results
		// Store selected individuals in form
		form.individuals = [];
		form.codes = [];
		form.referenceGenome.name = genome;
		form.referenceGenome.version = version;

		for (const variety in checked) {
			if (checked[variety]) {
				form.individuals.push(...samples[variety].individuals);
				form.codes.push(...samples[variety].codes);
			}
		}
		form.region.chr = chromosome;
		form.region.start = start;
		form.region.end = end;
		if (customVCF && customTBI) {
			form["vcfFile"] = URL.createObjectURL(customVCF);
			form["tbiFile"] = URL.createObjectURL(customTBI);
			form["individuals"] = [];
			form["codes"] = [];
		}
		else if (version !== "" && config[genome]["versions"]) {
			form["vcfFile"] = config[genome]["versions"][version].files[chromosome];
			form["tbiFile"] = form["vcfFile"] + ".tbi";
		}
		else {
			form["vcfFile"] = "";
			form["tbiFile"] = "";
		}
		// If we have VCF but no TBI, show a toast error
		if (customVCF && !customTBI) {
			alert("No TBI file selected for the VCF. Your selected VCF file will not be used.");
		}

		form["fastaFile"] = config[genome].files['fastaFile'];

		form["faiFile"] = config[genome].files['faiFile'];
		if (config[genome].files['annotations'])
			form["annotations"] = config[genome].files['annotations'];
		form["filters"] = {
			"minDepth": minDepth,
			"useSNPs": useSNPs,
			"useIndels": useIndels
		};
		onFormUpdated(form); // Update form

		generateSequences(form, setProgress).then(([fastas, offsets]) => {
			form.offsets = offsets;
			setProgress(100);
			// Load annotations
			fetch(form.annotations)
				.then((response) => response.json())
				.then((json) => {
					if (json[form.region.chr])
						onAnnotationsLoaded(json[form.region.chr])
				})
				.catch((err) => { console.log(err) });
			onFastaGenerated(fastas);
		}).catch((err) => { console.log(err) });
	};
	const addLocalGenome = () => {
		config[customFASTA.name] = {
			"files": {
				"fastaFile": URL.createObjectURL(customFASTA),
				"faiFile": URL.createObjectURL(customFAI)
			},
		};
	}
	return (
		<div className="MainView mx-2 d-flex flex-column justify-content-center align-items-center">
			<Card className="m-2 shadow card text-bg-dark mb-3">
				<div className="CardContent mx-4 mb-4 mt-3">
					<Form noValidate>
						<FormGroup className="mb-4">
							<Form.Label>Select a reference genome:</Form.Label>
							<InputGroup>
								<Form.Select value={genome} onChange={(e) => { setGenome(e.target.value) }}>
									{Object.keys(config).map((genome) => { return <option key={genome}>{genome}</option> })}
								</Form.Select>
								<div className="modal fade" data-bs-theme="dark" id="genomeModal" tabIndex="-1" aria-labelledby="genomeModalLabel" aria-hidden="true">
									<div className="modal-dialog">
										<div className="modal-content">
											<div className="modal-header">
												<h1 className="modal-title fs-5" id="genomeModalLabel">Load a genome from disk</h1>
												<button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
											</div>
											<div className="modal-body" data-bs-theme="light">
												<div className="mb-3">
													<label htmlFor="formFile" className="form-label">FASTA file</label>
													<input className="form-control" type="file" id="formFile" accept=".fasta, .fa" onChange={(e) => setCustomFASTA(e.target.files[0])} />
												</div>
												<div className="mb-3">
													<label htmlFor="formFile" className="form-label">FASTA index file (.fai)</label>
													<input className="form-control" type="file" id="formFile" accept=".fai" onChange={(e) => setCustomFAI(e.target.files[0])} />
												</div>
											</div>
												<button 
													type="button"
													data-bs-dismiss="modal"
													className={"btn btn-primary "+(validCustom ? "": "disabled")}
													style={{width: "100%", borderTopLeftRadius: "0", borderTopRightRadius: "0"}}
													onClick={() => {addLocalGenome(); setGenome(customFASTA.name)}}
												>
													Load
												</button>
										</div>
									</div>
								</div>
								{/*<p className="mx-4 my-2" >
									or
								</p>
								<button type="button" className="btn btn-primary" data-bs-toggle="modal" data-bs-target="#genomeModal">
									Load local genome
								</button>*/}

								
							</InputGroup>
							{/*<Form.Text className="text-light float-end mx-1">
								<em>
									More species and analyses will be added in
									the future.
								</em>
							</Form.Text>*/}
						</FormGroup>
						<FormGroup className="mb-4">
							<Form.Label>Enter a genomic region:</Form.Label>
							<InputGroup hasValidation>
								<InputGroup.Text>Group</InputGroup.Text>
								<Form.Group>
									<Form.Select
										style={{ borderRadius: "0px" }}
										onChange={(e) => setChromosome(e.target.value)}
										value={chromosome}
									>
										{chromosomes.map((chr) => { return <option key={chr}>{chr}</option> })}
									</Form.Select>
								</Form.Group>
								<InputGroup.Text>Start</InputGroup.Text>
								<FormGroup>
									<Form.Control
										value={start}
										style={{ width: "10em", borderRadius: "0px" }}
										onChange={(e) => {
											setStart(e.target.value !== "" ? parseInt(e.target.value) : 0)
										}}
										isValid={startValidated}
										isInvalid={!startValidated}
									/>
									<Form.Control.Feedback tooltip type="invalid">
										Chromosome start position must be between 1 and end position
									</Form.Control.Feedback>
								</FormGroup>


								<InputGroup.Text style={{ borderRadius: "0px" }}>End</InputGroup.Text>
								<Form.Group>
									<Form.Control
										value={end}
										style={{ width: "10em", borderStartStartRadius: "0px", borderEndStartRadius: "0px" }}
										onChange={(e) => {
											setEnd(e.target.value !== "" ? parseInt(e.target.value) : 0);
										}}
										isValid={endValidated}
										isInvalid={!endValidated}
									/>
									{config[genome] && <Form.Control.Feedback tooltip type="invalid">
										Chromosome end position must be between start position and {chrLengths[chromosome]}
									</Form.Control.Feedback>}
								</Form.Group>
							</InputGroup>
							{false &&
								<Form.Text className="text-light float-end mx-1">
									<em>
										Chromosome {form.region.chr} size: {chrLengths[form.region.chr]}
									</em>
								</Form.Text>
							}
						</FormGroup>
						<div>
							<ul className="nav nav-underline nav-fill" role="tablist" data-bs-theme="dark">
								<li className="nav-item" >
									<a className="nav-link text-light active" id="analyses-tab" data-bs-toggle="tab" data-bs-target="#analyses-tab-pane" type="button" role="tab" aria-controls="analyses-tab-pane">
										Precomputed Analyses
									</a>
								</li>
								<li className="nav-item">
									<a className="nav-link text-light" id="custom-tab" data-bs-toggle="tab" data-bs-target="#custom-tab-pane" type="button" role="tab" aria-controls="custom-tab-pane">
										Upload .VCF file
									</a>
								</li>
							</ul>
							<div className="tab-content px-2 py-2">

								<div className="tab-pane fade active show" id="analyses-tab-pane" role="tabpanel" tabIndex="0">
									{(samples && samples.length > 0) ?
										<FormGroup>
											<Form.Label>
												Select the analysis version:
											</Form.Label>
											<Form.Select onChange={(e) => { setVersion(e.target.value) }}>
												{config[genome]["versions"] && Object.keys(config[genome]["versions"]).map((version) => { return <option key={version}>{version}</option> })}
											</Form.Select>
											<Form.Label className="my-2">
												Select the desired individuals:
											</Form.Label>
											<FloatingLabel
												className="text-body"
												label="Filter by species"
												controlId="floatingSelect"
											>
												<Form.Select
													onChange={(e) => {
														setSelected(e.target.value);
													}}
													value={selected}
												>
													{species.map((specie, index) => {
														return (
															<option key={index}>
																{specie}
															</option>
														);
													})}
												</Form.Select>
											</FloatingLabel>
											<Form.Check
												className="my-2 mx-1"
												type="checkbox"
												label="Select all below"
												checked={checkedAll}
												onChange={(e) => {
													selectAll(e.target.checked);
												}}
											/>
											<ListGroup className="scrollbox">
												{samples.map((variant, index) => {
													return (
														(selected === "All species" ||
															selected === variant.species) && (
															<InputGroup key={index}>
																<div
																	className="input-group-text"
																	style={{
																		borderRadius: "0px",
																	}}
																>
																	<input
																		className="form-check-input mt-0 NoBorderRadius"
																		type="checkbox"
																		key={index}
																		value=""
																		aria-label="Checkbox for following text input"
																		onChange={() =>
																			toggleCheck(index)
																		}
																		checked={checked[index]}
																	/>
																</div>
																<Form.Control
																	className="IndivName"
																	disabled
																	readOnly
																	placeholder={
																		variant.species +
																		(variant.variety &&
																			": " +
																			variant.variety)
																	}
																	style={{
																		backgroundColor:
																			"var(--bs-body-bg)",
																	}}
																></Form.Control>
																<InputGroup.Text
																	className="NoBorderRadius fst-italic"
																	style={{
																		minWidth: "13vh",
																	}}
																>
																	{variant.individuals[0]
																		.split("_")
																		.slice(0, -1)
																		.join("_")}
																	{
																		" (" + variant.individuals.length + " individuals)"
																	}
																</InputGroup.Text>
															</InputGroup>
														)
													);
												})}
											</ListGroup>
										</FormGroup> :
										<div className="text-light text-center my-2">
											No analyses are available for this reference genome. Please use a custom .VCF file.
										</div>
									}
								</div>
								<div className={"tab-pane fade my-2"} id="custom-tab-pane" role="tabpanel" tabIndex="1">
									<div className="mb-3">
										<label htmlFor="formFile" className="form-label">.VCF.gz file computed with the reference genome <i>{genome}</i></label>
										<input className="form-control" type="file" id="formFile" accept=".vcf.gz" onChange={(e) => setCustomVCF(e.target.files[0])} />
									</div>
									<div className="mb-3">
										<label htmlFor="formFile" className="form-label">.TBI file for the selected VCF.gz</label>
										<input className="form-control" type="file" id="formFile" accept=".tbi" onChange={(e) => setCustomTBI(e.target.files[0])} />
									</div>
								</div>
							</div>
						</div>

						<div className="accordion accordion-flush mt-3 " >
							<div className="accordion-item bg-dark text-light">
								<h2 className="accordion-header flex text-center">
									<button className="btn btn-outline-light text-center" style={{ width: "90%" }} type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne" aria-expanded="false" aria-controls="collapseOne">
										Advanced Settings
									</button>
								</h2>
								<div className="accordion-collapse collapse bg-dark text-light mx-4 my-2" id="collapseOne">
									<InputGroup>
										<InputGroup.Text>
											Variant Sample Minimum Read Depth
										</InputGroup.Text>
										<Form.Control className="text-end" value={minDepth} onChange={(e) => { if (!isNaN(parseInt(e.target.value))) setMinDepth(parseInt(e.target.value)) }} />
									</InputGroup>
									<InputGroup className="mt-2">
										<InputGroup.Text>
											<Form.Check
												checked={useSNPs}
												onChange={(e) => setUseSNPs(e.target.checked)}
											/>
										</InputGroup.Text>
										<Form.Control
											className="IndivName"
											disabled
											readOnly
											placeholder="Use SNPs"
											style={{
												backgroundColor:
													"var(--bs-body-bg)",
											}} />
										<InputGroup.Text>
											<Form.Check
												checked={useIndels}
												onChange={(e) => setUseIndels(e.target.checked)}
											/>
										</InputGroup.Text>
										<Form.Control
											className="IndivName"
											disabled
											readOnly
											placeholder="Use Insertions and Deletions"
											style={{
												backgroundColor:
													"var(--bs-body-bg)",
											}} />
									</InputGroup>
								</div>
							</div>
						</div>

					</Form>
				</div>
				{/*<ProgressBar animated now={progress} className="progress-bottom bg-dark" />*/}
				<div className="progress progress-bottom bg-dark" style={{ height: '10px', borderTopLeftRadius: '0px', borderTopRightRadius: '0px' }}>
					<div id="progress" className="progress-bottom progress-bar progress-bar-striped progress-bar-animated " style={{ width: progress + '%' }} role="progressbar" aria-valuenow="75" aria-valuemin="0" aria-valuemax="100"></div>
				</div>
			</Card>
			<ButtonGroup>
				<Button variant="dark" onClick={submit} style={{ width: "50vw", maxWidth: "600px" }} disabled={!(startValidated && endValidated)}>
					Submit
				</Button>
			</ButtonGroup>

		</div>
	);
}

export default MainView;
